/**
 * WLED Background Service
 * Manages WLED device synchronization with scene light configs
 * 
 * Similar to HueBackgroundService, this service:
 * - Runs only on Tauri desktop builds
 * - Subscribes to light state changes via presence service
 * - Applies light states to WLED devices when this client is the leader
 */

import { Platform } from '@/lib/platform'
import { wledLogger, wledDeviceService } from './WledDeviceService'
import { lightPresenceService, type LightState } from '../realtime/LightPresenceService'
import { useLightsStore, type Light } from '@/stores/lights'
import { useWledDevicesStore, type WledDevice } from '@/stores/wledDevices'
import { useScenesStore } from '@/stores/scenes'
import { useSceneLightConfigsStore, type LightState as ConfigLightState } from '@/stores/sceneLightConfigs'
import { getDataProvider, isStandaloneMode, TABLES } from '@/lib/dataProvider'
import { hueHsbToRgb, type WledEffectState } from '@/types/wledEffect'

export interface WledServiceStatus {
  isRunning: boolean
  activeDevices: number
  lastSync: number | null
  platform: string
  capabilities: {
    localControl: boolean
    backgroundSync: boolean
  }
}

export class WledBackgroundService {
  private isRunning = false
  private wledDevicesStore: ReturnType<typeof useWledDevicesStore> | null = null
  private lightsStore: ReturnType<typeof useLightsStore> | null = null
  private scenesStore: ReturnType<typeof useScenesStore> | null = null
  private sceneLightConfigsStore: ReturnType<typeof useSceneLightConfigsStore> | null = null
  
  // Track pending writes to avoid flooding devices
  private pendingWrites = new Map<string, {
    state: ConfigLightState
    lastWrite: number
  }>()
  
  // Track pending DB persistence (to save colors across scene changes)
  private dbPersistenceQueue = new Map<string, ConfigLightState>()
  
  private writeInterval: NodeJS.Timeout | null = null
  private dbPersistenceInterval: NodeJS.Timeout | null = null
  private connectivityCheckInterval: NodeJS.Timeout | null = null
  
  private static readonly WRITE_INTERVAL = 100 // 100ms between writes to same device
  private static readonly DB_PERSISTENCE_INTERVAL = 2000 // 2 seconds between DB writes
  private static readonly CONNECTIVITY_CHECK_INTERVAL = 30000 // 30 seconds

  constructor() {
    // Initialize stores if in Vue context
    try {
      this.wledDevicesStore = useWledDevicesStore()
      this.lightsStore = useLightsStore()
      this.scenesStore = useScenesStore()
      this.sceneLightConfigsStore = useSceneLightConfigsStore()
    } catch {
      // Will be initialized later when called from Vue context
    }
  }

  /**
   * Check if this service should manage WLED sync
   */
  get isManagingSync(): boolean {
    return this.isRunning
  }

  /**
   * Initialize the WLED background service
   */
  async initialize(): Promise<void> {
    // WLED control requires local network access - only works in Tauri
    if (Platform.isWeb()) {
      wledLogger.info('Skipping WLED service - not available in web browser')
      return
    }

    if (this.isRunning) {
      wledLogger.warn('WLED background service already running')
      return
    }

    try {
      this.isRunning = true
      wledLogger.info('Starting WLED background service on', Platform.getPlatformName())

      // Initialize stores if not already done
      if (!this.wledDevicesStore) {
        this.wledDevicesStore = useWledDevicesStore()
      }
      if (!this.lightsStore) {
        this.lightsStore = useLightsStore()
      }
      if (!this.scenesStore) {
        this.scenesStore = useScenesStore()
      }
      if (!this.sceneLightConfigsStore) {
        this.sceneLightConfigsStore = useSceneLightConfigsStore()
      }

      // Load existing WLED devices
      await this.wledDevicesStore.fetchDevices()

      // Subscribe to presence state changes
      wledLogger.debug('Subscribing to presence state changes...')
      lightPresenceService.onStateChange(this.handlePresenceStateChange.bind(this))
      wledLogger.success('Presence subscription active')

      // Start write interval (processes pending writes)
      this.startWriteInterval()
      
      // Start DB persistence interval (saves colors to scene_light_configs)
      this.startDbPersistenceInterval()

      // Start connectivity check
      this.startConnectivityCheck()

      wledLogger.success('WLED background service started successfully')
    } catch (error) {
      wledLogger.error('Failed to initialize WLED background service:', error)
      this.isRunning = false
    }
  }

  /**
   * Shutdown the background service
   */
  async shutdown(): Promise<void> {
    if (!this.isRunning) {
      return
    }

    wledLogger.info('Shutting down WLED background service')
    this.isRunning = false

    // Stop intervals
    if (this.writeInterval) {
      clearInterval(this.writeInterval)
      this.writeInterval = null
    }
    
    if (this.dbPersistenceInterval) {
      clearInterval(this.dbPersistenceInterval)
      this.dbPersistenceInterval = null
    }

    if (this.connectivityCheckInterval) {
      clearInterval(this.connectivityCheckInterval)
      this.connectivityCheckInterval = null
    }

    // Flush any pending DB writes before shutdown
    await this.flushDbWrites()

    // Clear pending writes
    this.pendingWrites.clear()
    this.dbPersistenceQueue.clear()

    wledLogger.success('WLED background service stopped')
  }

  /**
   * Track local state change (called by composables when state changes locally)
   * This mirrors HueBackgroundService.trackLocalStateChange
   */
  trackLocalStateChange(lightId: string, state: LightState): void {
    if (!this.isRunning) {
      wledLogger.debug(`📝 [WledBackgroundService] NOT running, ignoring state change for ${lightId}`)
      return
    }

    // Check if this light is a WLED light
    const light = this.lightsStore?.allLights.find(l => l.id === lightId)
    if (!light) {
      wledLogger.debug(`📝 [WledBackgroundService] Light not found: ${lightId}`)
      return
    }

    const wledDeviceId = (light as any).wled_device_id
    if (!wledDeviceId) {
      // Not a WLED light - silently ignore
      return
    }

    wledLogger.info(`📝 [WledBackgroundService] Tracked LOCAL change for WLED light ${light.name}:`, {
      on: state.on,
      brightness: state.brightness,
      hue: state.hue,
      saturation: state.saturation,
      ct: (state as any).ct
    })

    // Queue the write
    this.pendingWrites.set(lightId, {
      state: state as ConfigLightState,
      lastWrite: 0
    })
  }

  /**
   * Handle light state changes from presence service (from OTHER clients)
   */
  private async handlePresenceStateChange(payload: {
    lightId: string
    state: LightState
    timestamp: number
    clientId: string
    sceneId: string
  }): Promise<void> {
    if (!this.isRunning) return

    const { lightId, state } = payload

    // Check if this light is a WLED light
    const light = this.lightsStore?.allLights.find(l => l.id === lightId)
    if (!light) {
      return
    }

    // Check if the light is connected to a WLED device
    // WLED lights have wled_device_id set instead of hue_bridge_id
    const wledDeviceId = (light as any).wled_device_id
    if (!wledDeviceId) {
      return // Not a WLED light
    }

    wledLogger.debug(`Light state change for WLED light ${light.name}:`, state)

    // Queue the write
    this.pendingWrites.set(lightId, {
      state: state as ConfigLightState,
      lastWrite: 0 // Will be set on actual write
    })
  }

  /**
   * Start the write interval that processes pending writes
   */
  private startWriteInterval(): void {
    if (this.writeInterval) {
      clearInterval(this.writeInterval)
    }

    this.writeInterval = setInterval(() => {
      this.processPendingWrites()
    }, WledBackgroundService.WRITE_INTERVAL)
  }
  
  /**
   * Start the DB persistence interval that saves colors to scene_light_configs
   */
  private startDbPersistenceInterval(): void {
    if (this.dbPersistenceInterval) {
      clearInterval(this.dbPersistenceInterval)
    }

    this.dbPersistenceInterval = setInterval(() => {
      this.flushDbWrites()
    }, WledBackgroundService.DB_PERSISTENCE_INTERVAL)
  }
  
  /**
   * Flush pending DB writes to scene_light_configs
   */
  private async flushDbWrites(): Promise<void> {
    if (this.dbPersistenceQueue.size === 0) {
      return
    }
    
    const activeScene = this.scenesStore?.getActiveScene
    if (!activeScene) {
      this.dbPersistenceQueue.clear()
      return
    }
    
    wledLogger.debug(`Flushing ${this.dbPersistenceQueue.size} WLED light states to DB`)
    
    for (const [lightId, state] of this.dbPersistenceQueue.entries()) {
      try {
        await this.updateSceneLightConfig(activeScene.id, lightId, state)
      } catch (error) {
        wledLogger.error(`Failed to save WLED light ${lightId} to DB:`, error)
      }
    }
    
    wledLogger.debug(`Saved ${this.dbPersistenceQueue.size} WLED lights to DB`)
    this.dbPersistenceQueue.clear()
  }
  
  /**
   * Update scene_light_configs for a specific WLED light
   */
  private async updateSceneLightConfig(sceneId: string, lightId: string, state: ConfigLightState): Promise<void> {
    // Build target_state object
    const targetState: Record<string, unknown> = {
      on: state.on ?? true,
      brightness: state.brightness ?? 127
    }
    
    // Color mode - prefer xy, then hue/sat
    if (state.xy && Array.isArray(state.xy)) {
      targetState.xy = state.xy
    } else if (state.hue !== undefined && state.hue !== null && 
               state.saturation !== undefined && state.saturation !== null) {
      targetState.hue = state.hue
      targetState.saturation = state.saturation
    }
    
    // Color temperature mode - support both 'ct' (from presence) and 'color_temperature' (from DB)
    const ct = (state as any).ct ?? state.color_temperature
    if (ct !== undefined && ct !== null) {
      targetState.color_temperature = ct
    }
    
    // Upsert scene_light_configs
    const provider = getDataProvider()
    const result = await provider.upsert(TABLES.SCENE_LIGHT_CONFIGS, {
      scene_id: sceneId,
      light_id: lightId,
      target_state: targetState
    }, 'scene_id,light_id')
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to update scene light config')
    }
    
    // Also update local store
    if (this.sceneLightConfigsStore) {
      await this.sceneLightConfigsStore.fetchConfigs()
    }
  }

  /**
   * Process pending writes to WLED devices
   */
  private async processPendingWrites(): Promise<void> {
    if (!this.isRunning || this.pendingWrites.size === 0) {
      return
    }

    const now = Date.now()

    for (const [lightId, pending] of this.pendingWrites) {
      // Skip if we wrote too recently
      if (now - pending.lastWrite < WledBackgroundService.WRITE_INTERVAL) {
        continue
      }

      // Get light and device info
      const light = this.lightsStore?.allLights.find(l => l.id === lightId)
      if (!light) {
        this.pendingWrites.delete(lightId)
        continue
      }

      const wledDeviceId = (light as any).wled_device_id
      const segmentId = (light as any).wled_segment_id
      const device = this.wledDevicesStore?.allDevices.find(d => d.id === wledDeviceId)
      if (!device || !device.is_online) {
        this.pendingWrites.delete(lightId)
        continue
      }

      // Apply state to device (with segment if specified)
      await this.applyStateToDevice(device, pending.state, segmentId)
      
      // Update last write time
      pending.lastWrite = now
      
      // Queue for DB persistence (to save color across scene changes)
      this.dbPersistenceQueue.set(lightId, pending.state)
      
      // Remove from pending if write was successful
      this.pendingWrites.delete(lightId)
    }
  }

  /**
   * Apply a light state to a WLED device (optionally to a specific segment)
   */
  private async applyStateToDevice(
    device: WledDevice,
    state: ConfigLightState,
    segmentId?: number
  ): Promise<boolean> {
    try {
      // Convert brightness (1-254 Hue scale to 0-255 WLED scale)
      let brightness: number | undefined
      if (state.brightness !== undefined && state.brightness !== null) {
        brightness = Math.round((state.brightness / 254) * 255)
      }

      // Handle color - can be RGB [R,G,B] or RGBW [R,G,B,W]
      let color: [number, number, number] | [number, number, number, number] | undefined
      if (state.xy && Array.isArray(state.xy)) {
        color = this.xyToRgb(state.xy[0], state.xy[1])
      } else if (state.hue !== undefined && state.hue !== null && 
                 state.saturation !== undefined && state.saturation !== null) {
        color = this.hsvToRgb(
          state.hue / 65535,
          state.saturation / 254,
          1
        )
      }

      // Handle color temperature - use RGBW white channel for warm white LEDs
      // Support both 'color_temperature' (from DB) and 'ct' (from presence/UI)
      let colorTemperature: number | undefined
      const ct = (state as any).ct ?? state.color_temperature
      if (ct !== undefined && ct !== null) {
        // ct/color_temperature is in Hue mirek (153-500), convert to Kelvin
        colorTemperature = Math.round(1000000 / ct)
        
        // For WLED devices with RGBW strips, use the white channel instead of RGB
        // This produces better quality white light from the dedicated warm white LED
        if (!color) {
          // Use pure white channel: [0, 0, 0, W] where W is max (brightness controlled separately)
          color = [0, 0, 0, 255]
          wledLogger.debug(`Using RGBW white channel for CT ${ct} mirek = ${colorTemperature}K`)
        }
      }

      const transitionTime = 400

      // Use segment-specific API if segment ID is provided
      if (segmentId !== undefined && segmentId !== null) {
        wledLogger.debug(`Applying to ${device.name} segment ${segmentId}:`, { on: state.on, brightness, color, colorTemperature })

        const success = await wledDeviceService.setSegmentState(device.ip_address, segmentId, {
          on: state.on,
          brightness,
          color,
          colorTemperature,
          transitionTime
        })

        if (success) {
          wledLogger.debug(`Successfully updated ${device.name} segment ${segmentId}`)
        } else {
          wledLogger.warn(`Failed to update ${device.name} segment ${segmentId}`)
        }

        return success
      } else {
        // Legacy: apply to whole device (segment 0)
        wledLogger.debug(`Applying to ${device.name} (whole device):`, { on: state.on, brightness, color, colorTemperature })

        const success = await wledDeviceService.setSolidColor(device.ip_address, {
          on: state.on ?? true,
          brightness,
          color,
          colorTemperature,
          transitionTime
        })

        if (success) {
          wledLogger.debug(`Successfully updated ${device.name}`)
        } else {
          wledLogger.warn(`Failed to update ${device.name}`)
        }

        return success
      }
    } catch (error) {
      wledLogger.error(`Error applying state to ${device.name}:`, error)
      return false
    }
  }
  
  /**
   * Convert color temperature (Kelvin) to warm white RGB color
   * This approximates CT as an RGB color for WLED devices without CCT support
   */
  private ctToWarmWhiteRgb(kelvin: number): [number, number, number] {
    // Clamp to reasonable range
    const k = Math.max(1000, Math.min(10000, kelvin)) / 100
    
    let r: number, g: number, b: number
    
    // Red component
    if (k <= 66) {
      r = 255
    } else {
      r = k - 60
      r = 329.698727446 * Math.pow(r, -0.1332047592)
      r = Math.max(0, Math.min(255, r))
    }
    
    // Green component
    if (k <= 66) {
      g = k
      g = 99.4708025861 * Math.log(g) - 161.1195681661
      g = Math.max(0, Math.min(255, g))
    } else {
      g = k - 60
      g = 288.1221695283 * Math.pow(g, -0.0755148492)
      g = Math.max(0, Math.min(255, g))
    }
    
    // Blue component
    if (k >= 66) {
      b = 255
    } else if (k <= 19) {
      b = 0
    } else {
      b = k - 10
      b = 138.5177312231 * Math.log(b) - 305.0447927307
      b = Math.max(0, Math.min(255, b))
    }
    
    return [Math.round(r), Math.round(g), Math.round(b)]
  }

  /**
   * Start connectivity check interval
   */
  private startConnectivityCheck(): void {
    if (this.connectivityCheckInterval) {
      clearInterval(this.connectivityCheckInterval)
    }

    this.connectivityCheckInterval = setInterval(() => {
      this.checkDeviceConnectivity()
    }, WledBackgroundService.CONNECTIVITY_CHECK_INTERVAL)

    // Run initial check
    this.checkDeviceConnectivity()
  }

  /**
   * Check connectivity to all WLED devices
   */
  private async checkDeviceConnectivity(): Promise<void> {
    if (!this.wledDevicesStore) return

    const devices = this.wledDevicesStore.allDevices

    for (const device of devices) {
      const isOnline = await wledDeviceService.isWledDevice(device.ip_address)
      
      if (isOnline !== device.is_online) {
        wledLogger.info(`Device ${device.name} is now ${isOnline ? 'online' : 'offline'}`)
        await this.wledDevicesStore.updateDeviceStatus(device.id, isOnline)
      }
    }
  }

  /**
   * Get service status
   */
  getStatus(): WledServiceStatus {
    return {
      isRunning: this.isRunning,
      activeDevices: this.wledDevicesStore?.allDevices.filter(d => d.is_online).length || 0,
      lastSync: null, // TODO: Track this
      platform: Platform.getPlatformName(),
      capabilities: {
        localControl: Platform.isDesktop(),
        backgroundSync: Platform.isDesktop()
      }
    }
  }

  /**
   * Update active scene (call this when user changes scenes)
   * Applies saved scene configs to WLED devices
   */
  async setActiveScene(sceneId: string | null): Promise<void> {
    if (!sceneId) {
      wledLogger.debug('No scene ID provided, skipping WLED scene activation')
      return
    }

    wledLogger.info(`Activating WLED scene: ${sceneId}`)

    // Get all WLED lights
    const wledLights = this.lightsStore?.allLights.filter(
      (l) => (l as any).wled_device_id
    ) || []

    if (wledLights.length === 0) {
      wledLogger.debug('No WLED lights found')
      return
    }

    // Get resolved configs for this scene (includes inheritance)
    const resolvedConfigs = this.sceneLightConfigsStore?.getResolvedConfigsForScene(sceneId) || []

    for (const light of wledLights) {
      const resolvedConfig = resolvedConfigs.find(c => c.light_id === light.id)
      
      // Check if we have WLED effect settings (gradient, effect, etc.)
      const effectSettings = resolvedConfig?.wled_effect_settings as WledEffectState | undefined
      
      // Use effect settings only if effectId is defined AND not Solid (0)
      // For Solid mode (effectId = 0), use target_state like normal color lights
      if (effectSettings && effectSettings.effectId !== undefined && effectSettings.effectId !== 0) {
        // Apply WLED effect with gradient colors
        wledLogger.debug(`Applying ${resolvedConfig?.is_inherited ? 'inherited' : 'direct'} effect to WLED light ${light.name}`)
        await this.applyEffectToDevice(light, effectSettings)
      } else if (effectSettings?.effectId === 0 && resolvedConfig?.target_state) {
        // Solid mode - use target_state for color (single pip saves here)
        wledLogger.debug(`Applying solid mode from target_state to WLED light ${light.name}`)
        
        const wledDevice = this.wledDevicesStore?.getDevice((light as any).wled_device_id)
        if (wledDevice) {
          const segmentId = (light as any).wled_segment_id ?? 0
          
          // Convert target_state to RGB color
          let color: [number, number, number] | undefined
          if (resolvedConfig.target_state.xy && Array.isArray(resolvedConfig.target_state.xy)) {
            color = this.xyToRgb(resolvedConfig.target_state.xy[0], resolvedConfig.target_state.xy[1])
          } else if (resolvedConfig.target_state.hue !== undefined && resolvedConfig.target_state.saturation !== undefined) {
            color = this.hsvToRgb(
              (resolvedConfig.target_state.hue as number) / 65535,
              (resolvedConfig.target_state.saturation as number) / 254,
              1
            )
          }
          
          if (color) {
            wledLogger.debug(`Solid mode color:`, color)
            await wledDeviceService.setSegmentEffect(
              wledDevice.ip_address,
              segmentId,
              {
                effectId: 0,
                speed: 128,
                intensity: 128,
                colors: [color],
                transitionTime: 400
              }
            )
          }
        }
      } else if (resolvedConfig?.target_state) {
        // Apply solid color (existing logic)
        wledLogger.debug(`Applying scene config to WLED light ${light.name}:`, resolvedConfig.target_state)
        
        // Build ConfigLightState from target_state
        const state: ConfigLightState = {
          on: resolvedConfig.target_state.on ?? true,
          brightness: resolvedConfig.target_state.brightness
        }
        
        // Handle color
        if (resolvedConfig.target_state.xy && Array.isArray(resolvedConfig.target_state.xy)) {
          state.xy = resolvedConfig.target_state.xy as [number, number]
        } else if (resolvedConfig.target_state.hue !== undefined && resolvedConfig.target_state.saturation !== undefined) {
          state.hue = resolvedConfig.target_state.hue as number
          state.saturation = resolvedConfig.target_state.saturation as number
        }
        
        // Handle color temperature
        if (resolvedConfig.target_state.color_temperature !== undefined && resolvedConfig.target_state.color_temperature !== null) {
          state.color_temperature = resolvedConfig.target_state.color_temperature
        }
        
        // Queue for device write
        this.pendingWrites.set(light.id, {
          state,
          lastWrite: 0
        })
        
        // Update current_state so pips show correct position
        // Note: HueLightState uses 'sat' not 'saturation', and 'ct' not 'color_temperature'
        const stateUpdate: Record<string, unknown> = {
          on: state.on,
          bri: state.brightness // Use 'bri' for HueLightState
        }
        if (state.xy) {
          stateUpdate.xy = state.xy
        }
        if (state.hue !== undefined && state.saturation !== undefined) {
          stateUpdate.hue = state.hue
          stateUpdate.sat = state.saturation // Use 'sat' not 'saturation'
        }
        if (state.color_temperature !== undefined && state.color_temperature !== null) {
          stateUpdate.ct = state.color_temperature // Use 'ct' not 'color_temperature'
        }
        
        this.lightsStore?.updateCurrentState(light.id, stateUpdate)
      } else {
        wledLogger.debug(`No scene config found for WLED light ${light.name}`)
      }
    }

    // Process writes immediately
    await this.processPendingWrites()
    
    wledLogger.success(`Applied scene configs to ${wledLights.length} WLED lights`)
  }

  /**
   * Apply effect settings to a specific light
   * Used by external components like SceneLightsCanvas when dragging gradient pips
   */
  async applyEffectSettingsToLight(lightId: string, effectSettings: WledEffectState): Promise<void> {
    const light = this.lightsStore?.getLight(lightId)
    if (!light) {
      wledLogger.warn(`Light not found: ${lightId}`)
      return
    }
    await this.applyEffectToDevice(light, effectSettings)
  }

  /**
   * Apply WLED effect settings to a device
   */
  private async applyEffectToDevice(light: Light, effectSettings: WledEffectState): Promise<void> {
    const wledDevice = this.wledDevicesStore?.getDevice((light as any).wled_device_id)
    if (!wledDevice) {
      wledLogger.warn(`No WLED device found for light ${light.name}`)
      return
    }

    const segmentId = (light as any).wled_segment_id ?? 0
    
    // Convert gradient stops to WLED colors (up to 3 colors)
    const colors: number[][] = []
    if (effectSettings.gradient?.stops && effectSettings.gradient.stops.length > 0) {
      const stops = effectSettings.gradient.stops
      for (let i = 0; i < Math.min(3, stops.length); i++) {
        const stop = stops[i]
        colors.push(hueHsbToRgb(stop.color.hue, stop.color.saturation, stop.color.brightness))
      }
      // Pad to 3 colors if needed
      while (colors.length < 3 && colors.length > 0) {
        colors.push(colors[colors.length - 1])
      }
    }

    wledLogger.info(`Applying effect ${effectSettings.effectName} (${effectSettings.effectId}) to ${light.name}`)
    
    await wledDeviceService.setSegmentEffect(
      wledDevice.ip_address,
      segmentId,
      {
        effectId: effectSettings.effectId,
        speed: effectSettings.speed,
        intensity: effectSettings.intensity,
        paletteId: effectSettings.paletteId,
        colors: colors.length > 0 ? colors : undefined,
        transitionTime: 400
      }
    )
    
    // Update current_state with the first gradient stop color (for pip display)
    if (effectSettings.gradient?.stops && effectSettings.gradient.stops.length > 0) {
      const firstStop = effectSettings.gradient.stops[0]
      this.lightsStore?.updateCurrentState(light.id, {
        on: true,
        hue: firstStop.color.hue,
        sat: firstStop.color.saturation,
        bri: firstStop.color.brightness
      })
    }
  }

  /**
   * Convert CIE xy color to RGB
   */
  private xyToRgb(x: number, y: number): [number, number, number] {
    // Assume brightness of 1.0 for conversion
    const Y = 1.0
    const X = (Y / y) * x
    const Z = (Y / y) * (1 - x - y)

    // XYZ to RGB (sRGB color space)
    let r = X * 1.656492 - Y * 0.354851 - Z * 0.255038
    let g = -X * 0.707196 + Y * 1.655397 + Z * 0.036152
    let b = X * 0.051713 - Y * 0.121364 + Z * 1.011530

    // Apply gamma correction
    r = r <= 0.0031308 ? 12.92 * r : 1.055 * Math.pow(r, 1 / 2.4) - 0.055
    g = g <= 0.0031308 ? 12.92 * g : 1.055 * Math.pow(g, 1 / 2.4) - 0.055
    b = b <= 0.0031308 ? 12.92 * b : 1.055 * Math.pow(b, 1 / 2.4) - 0.055

    // Clamp and scale to 0-255
    return [
      Math.max(0, Math.min(255, Math.round(r * 255))),
      Math.max(0, Math.min(255, Math.round(g * 255))),
      Math.max(0, Math.min(255, Math.round(b * 255)))
    ]
  }

  /**
   * Convert HSV to RGB
   */
  private hsvToRgb(h: number, s: number, v: number): [number, number, number] {
    let r = 0, g = 0, b = 0

    const i = Math.floor(h * 6)
    const f = h * 6 - i
    const p = v * (1 - s)
    const q = v * (1 - f * s)
    const t = v * (1 - (1 - f) * s)

    switch (i % 6) {
      case 0: r = v; g = t; b = p; break
      case 1: r = q; g = v; b = p; break
      case 2: r = p; g = v; b = t; break
      case 3: r = p; g = q; b = v; break
      case 4: r = t; g = p; b = v; break
      case 5: r = v; g = p; b = q; break
    }

    return [
      Math.round(r * 255),
      Math.round(g * 255),
      Math.round(b * 255)
    ]
  }
}

// Export singleton instance
export const wledBackgroundService = new WledBackgroundService()
